import {
  Alert,
  Box,
  Container,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { AsolviSwitch } from "components/AsolviSwitch";
import DecreaseButton from "components/DecreaseButton";
import { FilterAppBar } from "components/FilterAppBar";
import FullScreenDialog from "components/FullScreenDialog";
import IncreaseButton from "components/IncreaseButton";
import { OrderLineInfoInput } from "components/OrderLineInfoInput";
import PrimaryButton from "components/PrimaryButton";
import SecondaryButton from "components/SecondaryButton";
import StyledTextField from "components/StyledTextField";
import { InvoiceAutocomplete } from "components/job/InvoiceAutocomplete";
import { PartAutocomplete } from "components/parts/PartAutocomplete";
import { PartFilterDialog } from "components/parts/PartFilterDialog";
import { getInvoiceableDefaultValue } from "helpers";
import technicianSettings from "helpers/technicianSettings";
import { isEqual } from "lodash";
import { AddPartEnum } from "models/AddPartEnum";
import { defaultPartFilter, PartFilter, PartFilterKeys } from "models/PartFilter";
import { StockLocationType } from "models/StockLocationType";
import { UsedPart } from "models/usedPart";
import { Maybe, PartStatus, PartType, StockStore } from "operations/schema/schema";
import { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { selectConnectionStatus } from "store/root.store";
import {
  addPart,
  selectSelectedJob,
  selectSelectedJobVisit,
  updatePart,
} from "store/slices/jobs.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { isFlagEnabled } from "store/slices/user.store";

const PREFIX = "AddPart";

const classes = {
  flex: `${PREFIX}-flex`,
  toggleButtonGroup: `${PREFIX}-toggleButtonGroup`,
  toggleButton: `${PREFIX}-toggleButton`,
  radioButton: `${PREFIX}-radioButton`,
};

const StyledContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(2),

  [`& .${classes.flex}`]: {
    marginTop: theme.spacing(2),
    display: "flex",
  },

  [`& .${classes.toggleButtonGroup}`]: {
    width: "100%",
    height: "48px",
  },

  [`& .${classes.toggleButton}`]: {
    flex: 1,
    "&.Mui-selected": {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.info.main,
      "&:hover": {
        backgroundColor: theme.palette.common.white,
      },
      border: `1px solid ${theme.palette.info.main} !important`,
    },
  },

  [`& .${classes.radioButton}`]: {
    "&.Mui-checked": {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.info.main,
    },
  },
}));

interface AddPartProps {
  setOpenAddPartDialog: (open: boolean) => void;
  loading: boolean;
  stockCheckboxes: StockLocationType[];
  stockLocation: StockLocationType;
  setStockLocation: (location: StockLocationType) => void;
  dialogType: AddPartEnum;
  partSearch: string;
  setPartSearch: Dispatch<SetStateAction<string>>;
}

export const AddPart: FC<AddPartProps> = (props) => {
  const {
    partSearch,
    setPartSearch,
    loading,
    stockLocation,
    stockCheckboxes,
    setStockLocation,
    setOpenAddPartDialog,
  } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const isOnline = useAppSelector(selectConnectionStatus);
  const job = useAppSelector(selectSelectedJob);
  const { usedParts } = useAppSelector(selectSelectedJobVisit);
  const { userVar } = useAppSelector((state) => state.user);
  const {
    availableParts,
    requestableParts,
    suggestedParts,
    frequencyParts,
    loading: cacheLoading,
  } = useAppSelector((state) => state.cache);
  const hasUpdatedServiceJobEndpoint = useAppSelector((s) =>
    isFlagEnabled(s, "UpdatedServiceJobEndpoint"),
  );
  const allowNegativeStock = technicianSettings.allowNegativeStock ?? false;
  const allowChangeInvoiceType =
    technicianSettings.allowChangeInvoiceType && hasUpdatedServiceJobEndpoint;
  const [showSuggestedParts, setShowSuggestedParts] = useState(false);
  const [showFrequencyParts, setShowFrequencyParts] = useState(false);
  const [selectedPart, setSelectedPart] = useState<PartType | null>(null);
  const [qty, setQty] = useState<number>(1);
  const [tempQty, setTempQty] = useState<string>("1");
  const [inputError, setInputError] = useState<string>("");
  const [totalUsedPart, setTotalUsedPart] = useState<number>(0);
  const [partList, setPartList] = useState<PartType[]>([]);
  const [scannerValue, setScannerValue] = useState("");
  const [dialogType, setDialogType] = useState(props.dialogType);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [appliedFilter, setAppliedFilter] = useState<PartFilter>(defaultPartFilter);
  const [filter, setFilter] = useState<PartFilter>(defaultPartFilter);
  const [invoiceType, setInvoiceType] = useState<string | null>(
    getInvoiceableDefaultValue(job.contract?.typeObjNo),
  );
  const [info01, setInfo01] = useState<string | null>(null);
  const [info02, setInfo02] = useState<string | null>(null);
  const [info03, setInfo03] = useState<string | null>(null);
  const [info04, setInfo04] = useState<string | null>(null);
  const [tempDesc, setTempDesc] = useState<string>();

  const openEditDescription = hasUpdatedServiceJobEndpoint && selectedPart?.isNonStock;

  const filterActiveCount = useCallback(() => {
    let count = 0;
    for (const k of PartFilterKeys) {
      if (isEqual(defaultPartFilter[k], appliedFilter[k])) continue;
      count++;
    }
    return count;
  }, [appliedFilter]);

  const isAddPart = dialogType === AddPartEnum.AddPart;
  const machineId = job?.machine?.id ?? "";
  const modelGuid = job?.machine?.modelGuid ?? null;

  const modelFrequentParts = useMemo(() => {
    return modelGuid ? (frequencyParts[modelGuid] ?? []) : [];
  }, [frequencyParts, modelGuid]);

  const enableFreqList = technicianSettings.syncFreqList && modelFrequentParts.length > 0;

  const changeSelectedPart = useCallback(
    (selectedPart: PartType | null) => {
      setSelectedPart(selectedPart);
      if (selectedPart !== null) {
        const cachedPart = usedParts.filter(
          (p) =>
            p.part.articleNo === selectedPart.articleNo && p.part.stockId === selectedPart.stockId,
        );
        setTotalUsedPart(cachedPart.reduce((acc, p) => acc + (p ? p.part.quantity || 0 : 0), 0));
      }

      setQty(1);
      setTempQty("1");
      setTempDesc(selectedPart?.description ?? "");
    },
    [usedParts],
  );

  const changeQty = useCallback(
    (amount: number) => {
      const value = qty + amount;
      setQty(value);
      setTempQty(value.toString());
    },
    [qty],
  );

  const changeQtyFromInput = useCallback(
    (value: string) => {
      setTempQty(value);
      if (selectedPart !== null) {
        const numericValue = selectedPart.isNonStock ? parseFloat(value) : parseInt(value);
        var limit = selectedPart.quantity ?? 1000;
        if (
          selectedPart.isNonStock ||
          allowNegativeStock ||
          (numericValue <= (limit || 0) && numericValue + totalUsedPart <= (limit || 0))
        ) {
          setQty(numericValue);
          setInputError("");
        } else {
          if ((limit || 0) - totalUsedPart >= 1) {
            setInputError(
              intl.formatMessage(
                {
                  id: "part.quantityTooHigh",
                },
                {
                  qty: (limit || 0) - totalUsedPart,
                },
              ),
            );
          }
        }
      }
    },
    [allowNegativeStock, intl, selectedPart, totalUsedPart],
  );

  const changeInvoiceType = useCallback((value: string) => {
    setInvoiceType(value);
  }, []);

  const changeDescription = useCallback((value: string) => {
    setTempDesc(value);
  }, []);

  const getStockId = useCallback(
    (stockStore?: Maybe<StockStore>) => {
      switch (stockStore) {
        case StockStore.Engineer:
          return userVar?.stockId;
        case StockStore.Nonstock:
          return stockLocation.locationId;
        default:
          return "";
      }
    },
    [stockLocation.locationId, userVar?.stockId],
  );

  const getStoreLabel = useCallback(
    (part: PartType) => {
      if (part.stockStore === StockStore.Nonstock) {
        return `${StockStore.Nonstock} - ${stockLocation.type}`;
      }

      return part.stockStore;
    },
    [stockLocation.type],
  );

  const addPartCb = useCallback(() => {
    if (!selectedPart) return;
    const part: PartType = {
      articleNo: selectedPart.articleNo || "",
      barcode: selectedPart ? selectedPart.barcode : "",
      quantity: qty + totalUsedPart,
      stockStore: selectedPart.stockStore || StockStore.Engineer,
      stockId: selectedPart.stockId ?? getStockId(selectedPart.stockStore),
      storeLabel: getStoreLabel(selectedPart!),
      isNonStock: selectedPart.isNonStock,
      description: tempDesc,
      status: isAddPart ? PartStatus.Used : PartStatus.Requested,
      salesPrice: selectedPart.salesPrice,
      invoiceType: invoiceType,
      info01: info01,
      info02: info02,
      info03: info03,
      info04: info04,
    };
    const usedPart: UsedPart = {
      part,
      maxQuantity: selectedPart.quantity!,
      salesPriceChanged: false,
      pendingUpload: false,
      uploaded: false,
    };

    const existingPart = usedParts.find((p) => {
      return (
        p.part?.articleNo === selectedPart.articleNo &&
        p.part?.stockId === getStockId(selectedPart.stockStore)
      );
    });
    if (existingPart) {
      dispatch(updatePart({ part: usedPart }));
    } else {
      dispatch(addPart({ part: usedPart }));
    }

    setSelectedPart(null);
    dispatch(
      addSnackbarMessage({
        key: isAddPart ? "AddPart-success" : "RequestPart-success",
        argument: part.articleNo ?? "",
      }),
    );
  }, [
    dispatch,
    getStockId,
    getStoreLabel,
    info01,
    info02,
    info03,
    info04,
    invoiceType,
    isAddPart,
    qty,
    selectedPart,
    tempDesc,
    totalUsedPart,
    usedParts,
  ]);

  const closeDialog = useCallback(() => {
    setOpenAddPartDialog(false);
  }, [setOpenAddPartDialog]);

  const applyFilters = useCallback(
    (partList: PartType[]) => {
      let newPartList = partList;
      if (appliedFilter) {
        // Evatic Specific
        if (!!appliedFilter.selectedSuppliers?.length) {
          newPartList = newPartList.filter((p) =>
            appliedFilter.selectedSuppliers.map((s) => s.id).includes(p.supplierId!),
          );
        }
        if (!!appliedFilter.sortGroup1?.length) {
          newPartList = newPartList.filter(
            (p) => appliedFilter.sortGroup1.findIndex((x) => x.code === p.sortGroup1) !== -1,
          );
        }
        if (!!appliedFilter.sortGroup2?.length) {
          newPartList = newPartList.filter(
            (p) => appliedFilter.sortGroup2.findIndex((x) => x.code === p.sortGroup2) !== -1,
          );
        }
        if (!!appliedFilter.sortGroup3?.length) {
          newPartList = newPartList.filter(
            (p) => appliedFilter.sortGroup3.findIndex((x) => x.code === p.sortGroup3) !== -1,
          );
        }
        if (!!appliedFilter.sortGroup4?.length) {
          newPartList = newPartList.filter(
            (p) => appliedFilter.sortGroup4.findIndex((x) => x.code === p.sortGroup4) !== -1,
          );
        }
        if (!!appliedFilter.sortGroup5?.length) {
          newPartList = newPartList.filter(
            (p) => appliedFilter.sortGroup5.findIndex((x) => x.code === p.sortGroup5) !== -1,
          );
        }
      }
      return newPartList;
    },
    [appliedFilter],
  );

  const setAvailableParts = useCallback(() => {
    if (isAddPart) {
      let newPartList: PartType[] = [];
      if (stockLocation.type === StockStore.Engineer) {
        newPartList = availableParts.technicianParts;
      } else if (stockLocation.type === StockStore.Locational) {
        if (isOnline && partSearch) {
          newPartList = availableParts.liveSearchParts;
        } else {
          newPartList = availableParts.locationalParts;
        }
      }

      newPartList = newPartList.filter(
        (part: PartType) =>
          part.stockStore &&
          (stockLocation.type === part.stockStore ||
            (part.stockStore !== StockStore.Other && stockLocation.type !== part.stockStore)),
      );

      if (showSuggestedParts) {
        newPartList = newPartList.filter(
          (p) => suggestedParts[machineId]?.findIndex((x) => x === p.articleNo) !== -1,
        );
      }
      if (showFrequencyParts) {
        newPartList = newPartList.filter(
          (p) => modelFrequentParts.findIndex((x) => x === p.articleNo) !== -1,
        );
      }

      if (appliedFilter) {
        newPartList = applyFilters(newPartList);
      }

      //sorting is needed for groupping to avoid duplicates
      newPartList.sort((a, b) => {
        let fa = a.stockStore,
          fb = b.stockStore;

        if (fa && fa === StockStore.Nonstock) {
          //to make sure that nonstock is the last one
          return 1;
        }

        let aName = a.stockName?.toLowerCase(),
          bName = b.stockName?.toLowerCase();

        if (aName && bName) {
          if (aName < bName) {
            return -1;
          }
          if (aName > bName) {
            return 1;
          }
        }

        if (fa && fb) {
          if (fa < fb) {
            return -1;
          }
          if (fa > fb) {
            return 1;
          }
        }

        return 0;
      });

      setPartList(newPartList);
    }
  }, [
    isAddPart,
    stockLocation.type,
    showSuggestedParts,
    showFrequencyParts,
    appliedFilter,
    availableParts.technicianParts,
    availableParts.liveSearchParts,
    availableParts.locationalParts,
    isOnline,
    partSearch,
    suggestedParts,
    machineId,
    modelFrequentParts,
    applyFilters,
  ]);

  useEffect(() => {
    if (isAddPart) {
      setAvailableParts();
    } else {
      let checkedRequestableParts = requestableParts ?? [];

      if (showSuggestedParts) {
        checkedRequestableParts = checkedRequestableParts.filter(
          (p) => suggestedParts[machineId]?.findIndex((x) => x === p.articleNo) !== -1,
        );
      }
      if (showFrequencyParts) {
        checkedRequestableParts = checkedRequestableParts.filter(
          (p) => modelFrequentParts?.findIndex((x) => x === p.articleNo) !== -1,
        );
      }

      if (appliedFilter) {
        checkedRequestableParts = applyFilters(checkedRequestableParts);
      }
      setPartList(checkedRequestableParts);
    }
  }, [
    isAddPart,
    availableParts,
    requestableParts,
    setAvailableParts,
    showSuggestedParts,
    appliedFilter,
    applyFilters,
    suggestedParts,
    machineId,
    showFrequencyParts,
    frequencyParts,
    modelGuid,
    modelFrequentParts,
  ]);

  useEffect(() => {
    if (scannerValue) {
      let scanMatches = partList.filter(
        (part) => part.barcode === scannerValue || part.articleNo === scannerValue,
      );
      if (scanMatches.length === 0) {
        scanMatches = partList.filter(
          (part) =>
            part.barcode?.toLowerCase().includes(scannerValue) ||
            part.articleNo?.toLowerCase().includes(scannerValue),
        );
      }

      if (scanMatches.length === 1) {
        setSelectedPart(scanMatches[0]);
      } else {
        dispatch(addSnackbarMessage({ key: "No-barcode-match", argument: scannerValue }));
      }
      setScannerValue("");
    }
  }, [scannerValue, intl, partList, dispatch]);

  const submitButtonText = isAddPart ? (
    <FormattedMessage id="visit.addPart" />
  ) : (
    <FormattedMessage id="part.requestPart" />
  );

  const quantity = selectedPart?.quantity;
  const isNonStock = selectedPart?.isNonStock;

  const plusButtonDisabled =
    isAddPart &&
    !isNonStock &&
    !allowNegativeStock &&
    (qty === quantity ||
      totalUsedPart >= (quantity || 0) ||
      qty + totalUsedPart >= (quantity || 0));

  return (
    <StyledContainer data-testid="AddPartDialog">
      <FilterAppBar
        onClick={setFilterDialogOpen}
        filterCount={filterActiveCount()}
        foundCount={partList.length}
        type="parts"
      />
      <Grid container direction="column" spacing={2}>
        <Grid item>
          {isAddPart ? (
            <RadioGroup
              row
              value={stockLocation.type}
              onChange={(event) => {
                const sl = stockCheckboxes.find((x) => x.type === event.target.value);

                if (sl) {
                  setStockLocation(sl);
                  setPartSearch("");
                }
              }}
              name="stock-location"
            >
              {stockCheckboxes.map((checkbox, index) => (
                <FormControlLabel
                  key={`${checkbox.label}-${index}`}
                  value={checkbox.type}
                  control={<Radio className={classes.radioButton} />}
                  label={checkbox.label}
                  disabled={checkbox.disabled}
                />
              ))}
            </RadioGroup>
          ) : (
            <Box p={2.625} /> /*Request - Part */
          )}
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ pt: "10px", pb: "10px" }}
          >
            <Grid item>
              <Typography variant="body1">
                <FormattedMessage id="part.showSuggestedParts" />
              </Typography>
            </Grid>
            <Grid item>
              <AsolviSwitch
                key="suggestedParts"
                data-testid="AddPart-ShowSuggestedToggle"
                checked={showSuggestedParts}
                disabled={cacheLoading.suggestedParts}
                onClick={() => {
                  setShowSuggestedParts(!showSuggestedParts);
                }}
              />
            </Grid>
          </Grid>
          {enableFreqList && (
            <Grid
              container
              alignItems="center"
              justifyContent="space-between"
              sx={{ pt: "10px", pb: "10px" }}
            >
              <Grid item>
                <Typography variant="body1">
                  <FormattedMessage id="part.showFrequencyParts" />
                </Typography>
              </Grid>
              <Grid item>
                <AsolviSwitch
                  key="frequencyParts"
                  data-testid="AddPart-ShowFrequencyToggle"
                  checked={showFrequencyParts}
                  disabled={cacheLoading.frequencyParts}
                  onClick={() => {
                    setShowFrequencyParts(!showFrequencyParts);
                  }}
                />
              </Grid>
            </Grid>
          )}
          <div className={classes.flex}>
            <PartAutocomplete
              selectedPart={selectedPart}
              partList={partList}
              loading={loading}
              showQty={isAddPart}
              partSearch={partSearch}
              setPartSearch={setPartSearch}
              setScannerValue={setScannerValue}
              changeSelectedPart={changeSelectedPart}
            />
          </div>
        </Grid>
        <Grid item>
          <ToggleButtonGroup
            value={dialogType}
            exclusive
            onChange={(_, newType: AddPartEnum) => {
              if (newType !== null) {
                setDialogType(newType);
                setSelectedPart(null);
                setTempQty("1");
                setInputError("");
                setTempDesc("");
              }
            }}
            size="small"
            className={classes.toggleButtonGroup}
          >
            <ToggleButton
              value={AddPartEnum.AddPart}
              className={classes.toggleButton}
              data-testid="AddPart-UsePartButton"
            >
              {intl.formatMessage({
                id: "part.usePart",
              })}
            </ToggleButton>
            <ToggleButton
              value={AddPartEnum.RequestPart}
              className={classes.toggleButton}
              data-testid="AddPart-RequestPartButton"
            >
              {intl.formatMessage({
                id: "part.requestPart",
              })}
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
        {selectedPart && openEditDescription && (
          <Grid item container xs={12}>
            <StyledTextField
              fullWidth
              value={tempDesc}
              id="description"
              label={intl.formatMessage({ id: "general.description" })}
              onChange={(e: any) => changeDescription(e.target.value)}
              multiline
              rows={5}
            />
          </Grid>
        )}
        {allowChangeInvoiceType && (
          <Grid item container xs={12}>
            <InvoiceAutocomplete onChange={changeInvoiceType} value={invoiceType ?? null} />
          </Grid>
        )}

        {selectedPart &&
          (selectedPart.isNonStock ||
            allowNegativeStock ||
            totalUsedPart < (selectedPart?.quantity || 0) ||
            !isAddPart) && (
            <Grid item container direction="row" justifyContent="space-around" alignItems="center">
              <Grid item xs={2} textAlign="center">
                <DecreaseButton onClick={() => changeQty(-1)} />
              </Grid>
              <Grid item xs={7} textAlign="center">
                <StyledTextField
                  value={
                    !selectedPart.isNonStock &&
                    !allowNegativeStock &&
                    totalUsedPart >= (selectedPart.quantity || 0) &&
                    isAddPart
                      ? ""
                      : tempQty
                  }
                  id="general.quantity"
                  type="number"
                  margin="normal"
                  onChange={(event) => changeQtyFromInput(event.target.value)}
                  helperText={inputError}
                  error={inputError !== ""}
                />
              </Grid>
              <Grid item xs={2} textAlign="center">
                <IncreaseButton
                  disabled={plusButtonDisabled}
                  className={plusButtonDisabled ? "buttonDisabled" : ""}
                  onClick={() => changeQty(1)}
                />
              </Grid>
            </Grid>
          )}
        {isAddPart &&
          selectedPart &&
          !selectedPart.isNonStock &&
          totalUsedPart >= (selectedPart?.quantity || 0) &&
          !allowNegativeStock && (
            <Grid item>
              <Alert severity="error">
                {intl.formatMessage(
                  {
                    id: "visit.part.allPartsUsed",
                  },
                  {
                    qty: selectedPart.quantity,
                    description: selectedPart.description,
                  },
                )}
              </Alert>
            </Grid>
          )}
        <OrderLineInfoInput
          info01={info01}
          info02={info02}
          info03={info03}
          info04={info04}
          setInfo01={setInfo01}
          setInfo02={setInfo02}
          setInfo03={setInfo03}
          setInfo04={setInfo04}
        />
        <Grid item>
          <PrimaryButton
            key={`${
              selectedPart === null ||
              totalUsedPart >= (selectedPart.quantity || 0) ||
              allowNegativeStock
            }`}
            disabled={
              selectedPart === null ||
              (isAddPart &&
                !selectedPart.isNonStock &&
                totalUsedPart >= (selectedPart.quantity || 0) &&
                !allowNegativeStock)
            }
            onClick={addPartCb}
          >
            {submitButtonText}
          </PrimaryButton>
        </Grid>
        <Grid item>
          <SecondaryButton
            variant="contained"
            onClick={closeDialog}
            data-testid="AddPartDialogFinishButton"
          >
            {intl.formatMessage({
              id: "general.finish",
            })}
          </SecondaryButton>
        </Grid>
      </Grid>
      <FullScreenDialog
        title={`${job.externalId} / ${intl.formatMessage({
          id: "filter.setFilters",
        })}`}
        centerTitle
        goingBack
        open={filterDialogOpen}
        setOpen={setFilterDialogOpen}
        child={
          <PartFilterDialog
            filter={filter}
            setFilter={setFilter}
            onReset={() => {
              setFilter(defaultPartFilter);
            }}
            onSubmit={() => {
              setAppliedFilter(filter);
              setFilterDialogOpen(false);
            }}
          />
        }
      />
    </StyledContainer>
  );
};
